import { searchFormattedString } from './string';

export const FUZZY_SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
} as const;

/**
 * Re-rank MiniSearch hits so exact token matches always beat prefix/fuzzy expansions.
 * BM25 rewards rare terms, so a long unusual name that merely has the query as a prefix
 * (e.g. "Messidoro") can outscore an exact match on a common surname (e.g. "Messi").
 * BM25 score is kept as the tiebreaker within each tier.
 */
export const rankByExactMatch = <Hit extends { score: number; terms: string[] }>(query: string, hits: Hit[]): Hit[] => {
  // Split on any non-alphanumeric run so the query tokenises the same way MiniSearch does
  // (it splits on punctuation too). Otherwise "T. Henry" → ["t.", "henry"] never matches the
  // indexed terms ["t", "henry"] and the exact-match re-rank silently misses punctuated names.
  const queryTerms = searchFormattedString(query)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

  const countExactMatches = (terms: string[]) =>
    queryTerms.reduce((count, term) => count + (terms.includes(term) ? 1 : 0), 0);

  return hits
    .map(hit => ({ hit, exactMatches: countExactMatches(hit.terms) }))
    .sort((a, b) => b.exactMatches - a.exactMatches || b.hit.score - a.hit.score)
    .map(({ hit }) => hit);
};
